import CryptoJS from "crypto-js";
import { encryption_key, initialization_vector } from "@/libs/config";
import { isString } from "lodash";

const ENCRYPTION_KEY = encryption_key; // 32-byte key for AES-256
const IV = initialization_vector; // 16-byte IV (Fixed)

/**
 * @name encrypt
 * @description Encrypts a given text using AES-256-CBC encryption method with a 32-byte encryption key and 16-byte initialization vector (IV). It uses the provided key and IV from the configuration file.
 * @param {string} text - The plain text to be encrypted.
 * @returns {string} The encrypted text in hexadecimal format.
 * @throws {Error} If the encryption fails.
 */
const encrypt = (text) => {
  const encrypted = CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(text),
    CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY),
    {
      iv: CryptoJS.enc.Utf8.parse(IV),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );

  return encrypted.ciphertext.toString(CryptoJS.enc.Hex); // Full hex output
};

/**
 * @name decrypt
 * @description Decrypts an encrypted text using AES-256-CBC decryption method with a 32-byte encryption key and 16-byte initialization vector (IV). It uses provided key and IV from configuration file.
 * @param {string} encryptedText - The encrypted text in hexadecimal format.
 * @returns {string} The decrypted plain text.
 * @throws {Error} If decryption fails.
 */
const decrypt = (encryptedText) => {
  if (!encryptedText) {
    return null;
  }

  try {
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Hex.parse(encryptedText) },
      CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY),
      {
        iv: CryptoJS.enc.Utf8.parse(IV),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    const result = decrypted.toString(CryptoJS.enc.Utf8);
    
    // Check if the result is valid UTF-8
    if (result && result.length > 0) {
      return result;
    } else {
      console.warn('Decryption resulted in empty or invalid data');
      return null;
    }
  } catch (error) {
    console.warn('Decryption failed:', error.message);
    return null;
  }
};

/**
 * @name safeDecryptProfileUrl
 * @description Safely attempts to decrypt a profile URL, handling various formats and providing fallbacks
 * @param {string} encryptedUrl - The potentially encrypted profile URL
 * @returns {string} The decrypted URL or the original if decryption fails
 */
const safeDecryptProfileUrl = (encryptedUrl) => {
  if (!encryptedUrl) {
    return null;
  }

  try {
    // First try to decrypt
    const decrypted = decrypt(encryptedUrl);
    
    if (decrypted && decrypted.length > 0) {
      // Check if it's a valid URL (starts with http or /)
      if (decrypted.startsWith('http') || decrypted.startsWith('/')) {
        return decrypted;
      }
      
      // If it's not starting with / or http, it might be a relative path
      // Try adding a leading slash
      const withSlash = '/' + decrypted;
      return withSlash;
    }
    
    // If decryption fails or returns invalid data, check if it's already a valid URL
    if (encryptedUrl.startsWith('http') || encryptedUrl.startsWith('/')) {
      return encryptedUrl;
    }
    
    // If neither works, return null to trigger fallback
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * @name encryptDecryptJson
 * @description Encrypts or decrypts all values in a JSON object recursively. For encryption, it uses the `encrypt` function; for decryption, it uses the `decrypt` function.
 * @param {Object|Array} jsonObject - The JSON object whose values need to be encrypted or decrypted.
 * @param {string} type - The operation type, either 'encrypt' or 'decrypt'. Defaults to 'encrypt'.
 * @returns {Object} The JSON object with encrypted or decrypted values.
 * @throws {Error} If the encryption or decryption fails.
 */
const encryptDecryptJson = (jsonObject, type = "encrypt") => {
  if (Array.isArray(jsonObject)) {
    // If it's an array, map through the array and process each element
    return jsonObject.map((item) => encryptDecryptJson(item, type));
  } else if (jsonObject instanceof FormData) {
    const formData = new FormData();
    for (var pair of jsonObject.entries()) {
      const [key, value] = pair;
      let encValue = value;
      // Skip encryption if the key contains 'logo' or any form of 'image'
      if (
        !/logo|image/i.test(key) ||
        (/logo|image/i.test(key) && isString(encValue))
      ) {
        encValue = encryptDecryptJson(value, type);
      }

      formData.append(key, encValue);
    }

    return formData;
  } else if (typeof jsonObject === "object" && jsonObject !== null) {
    let encryptedJson = {}; // Initialize as an object

    for (let key in jsonObject) {
      if (jsonObject.hasOwnProperty(key)) {
        let value = jsonObject[key];
        encryptedJson[key] = encryptDecryptJson(value, type);
      }
    }
    return encryptedJson;
  } else if (typeof jsonObject === "string") {
    if (type === "decrypt") {
      // Plain ISO datetimes (e.g. created_at) are not encrypted — pass through
      if (/^\d{4}-\d{2}-\d{2}T/.test(jsonObject)) {
        return jsonObject;
      }
      // Only decrypt hex ciphertext strings
      if (!/^[0-9a-fA-F]+$/.test(jsonObject)) {
        return jsonObject;
      }
    }
    return type === "encrypt" ? encrypt(jsonObject) : decrypt(jsonObject);
  } else {
    return type === "encrypt" ? encrypt(jsonObject) : decrypt(jsonObject);
  }
};

export { encrypt, decrypt, encryptDecryptJson, safeDecryptProfileUrl };
